import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import ChatHeader from "./components/ChatHeader";
import PopularQuestions from "./components/PopularQuestions";
import ChatInput from "./components/ChatInput";
import EnvBadge from "./components/EnvBadge";
import { changeLocale } from "./i18n";
import { useTranslation } from "react-i18next";
import {
  postToHost,
  readQueryParams,
  resolvePublishableKey,
} from "./lib/bridge";
import { streamAvatarReply } from "./lib/sseClient";
import type { BridgeMessage, ChatMessage } from "./types/bridge";
import { tokens } from "./theme";

const styles: Record<string, CSSProperties> = {
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: "100vh",
    background: tokens.surface.canvas,
    fontFamily: tokens.font.family,
    color: tokens.text.primary,
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  body: {
    flex: 1,
    overflowY: "auto",
    padding: tokens.space[4],
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[3],
    background: tokens.surface.canvas,
  },
  bubbleUser: {
    alignSelf: "flex-end",
    maxWidth: "85%",
    background: tokens.gradient.accent,
    color: tokens.text.onBrand,
    padding: "10px 14px",
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} ${tokens.radius.bubbleTail} ${tokens.radius.lg}`,
    fontSize: tokens.font.size.md,
    lineHeight: tokens.font.lineHeight.body,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bubbleAvatar: {
    alignSelf: "flex-start",
    maxWidth: "85%",
    background: tokens.surface.elevated,
    color: tokens.text.secondary,
    padding: "10px 14px",
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} ${tokens.radius.lg} ${tokens.radius.bubbleTail}`,
    fontSize: tokens.font.size.md,
    lineHeight: tokens.font.lineHeight.body,
    border: `1px solid ${tokens.border.default}`,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bubbleSystem: {
    alignSelf: "center",
    maxWidth: "92%",
    background: tokens.surface.danger,
    color: tokens.text.danger,
    padding: "10px 14px",
    borderRadius: tokens.radius.md,
    fontSize: 13,
    lineHeight: 1.4,
    textAlign: "center",
    border: `1px solid ${tokens.border.danger}`,
  },
  typing: {
    alignSelf: "flex-start",
    fontSize: tokens.font.size.sm,
    color: tokens.text.muted,
    paddingLeft: tokens.space[1],
  },
  retryBtn: {
    marginTop: tokens.space[2],
    border: `1px solid ${tokens.color.danger[300]}`,
    background: tokens.surface.elevated,
    color: tokens.color.danger[700],
    borderRadius: tokens.radius.sm,
    padding: "6px 12px",
    fontSize: tokens.font.size.sm,
    fontWeight: tokens.font.weight.semibold,
    cursor: "pointer",
    fontFamily: "inherit",
  },
};

function createId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function shouldAutoOpen(): boolean {
  const isEmbedded = window.parent !== window;
  const preview = new URLSearchParams(window.location.search).has("preview");
  return !isEmbedded || preview;
}

export default function App() {
  const { t, i18n } = useTranslation();
  const { publishableKey } = readQueryParams();
  const autoOpen = shouldAutoOpen();
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [lastFailedText, setLastFailedText] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pkRef = useRef(publishableKey);

  const makeInitMessage = useCallback(
    (): ChatMessage => ({
      id: "init",
      text: t("chat.greeting"),
      sender: "avatar",
      status: "complete",
    }),
    [t],
  );

  const purgeConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setIsStreaming(false);
    setServiceUnavailable(false);
    setLastFailedText(null);
  }, []);

  const startFreshConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([makeInitMessage()]);
    setIsStreaming(false);
    setServiceUnavailable(false);
    setLastFailedText(null);
  }, [makeInitMessage]);

  const startFreshRef = useRef(startFreshConversation);
  startFreshRef.current = startFreshConversation;
  const purgeRef = useRef(purgeConversation);
  purgeRef.current = purgeConversation;

  // Seed / refresh the init greeting when locale (or t) changes.
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 0 && autoOpen) return [makeInitMessage()];
      if (prev.length === 1 && prev[0].id === "init")
        return [makeInitMessage()];
      return prev;
    });
  }, [autoOpen, makeInitMessage, i18n.language]);

  useEffect(() => {
    const handleBridgeMessages = (event: MessageEvent) => {
      const data = event.data as BridgeMessage;
      if (!data || data.source !== "avatar_host_script") return;

      if (data.event === "WIDGET_OPEN") {
        const key = resolvePublishableKey(data.payload);
        if (key) pkRef.current = key;
        if (data.payload.locale) {
          void changeLocale(data.payload.locale).then(() => {
            startFreshRef.current();
          });
        } else {
          startFreshRef.current();
        }
        setIsOpen(true);
      }

      if (data.event === "WIDGET_CLOSE") {
        setIsOpen(false);
        purgeRef.current(); // PRIVACY: zero state persisted
      }
    };

    window.addEventListener("message", handleBridgeMessages);

    // Tell host the panel is ready so it can (re)send WIDGET_OPEN after lazy iframe load.
    if (window.parent !== window) {
      postToHost("WIDGET_READY");
    }

    return () => {
      window.removeEventListener("message", handleBridgeMessages);
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isStreaming]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (serviceUnavailable || isStreaming) return;

      const userMsg: ChatMessage = {
        id: createId("user"),
        text,
        sender: "user",
        status: "complete",
      };
      const streamId = createId("avatar");
      const placeholder: ChatMessage = {
        id: streamId,
        text: "",
        sender: "avatar",
        status: "streaming",
      };

      setMessages((prev) => [...prev, userMsg, placeholder]);
      setIsStreaming(true);
      setLastFailedText(null);

      const controller = new AbortController();
      abortRef.current = controller;

      await streamAvatarReply({
        publishableKey: pkRef.current,
        message: text,
        signal: controller.signal,
        onChunk: (chunk) => {
          if (chunk.type === "token" && chunk.text) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamId ? { ...m, text: m.text + chunk.text } : m,
              ),
            );
          }

          if (chunk.type === "done") {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamId ? { ...m, status: "complete" } : m,
              ),
            );
            setIsStreaming(false);
          }

          if (chunk.type === "error") {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamId
                  ? {
                      ...m,
                      sender: "system",
                      status: "error",
                      text: chunk.reason || t("chat.errorFallback"),
                    }
                  : m,
              ),
            );
            setLastFailedText(text);
            setIsStreaming(false);
            postToHost("API_STREAM_ERROR", { reason: chunk.reason });
          }
        },
      });
    },
    [isStreaming, serviceUnavailable, t],
  );

  const handleReset = () => {
    if (isStreaming) return;
    startFreshConversation();
  };

  const showPopular = messages.length <= 1 && !isStreaming;

  if (!isOpen) return null;

  return (
    <div style={styles.root}>
      <EnvBadge />
      <ChatHeader onReset={handleReset} disabled={isStreaming} />
      <div ref={scrollRef} style={styles.body}>
        {showPopular && (
          <PopularQuestions
            onSelectQuestion={sendMessage}
            disabled={serviceUnavailable || isStreaming}
          />
        )}

        {!showPopular &&
          messages.map((msg) => {
            if (msg.sender === "system" || msg.status === "error") {
              return (
                <div key={msg.id} style={styles.bubbleSystem}>
                  <div>{msg.text}</div>
                  {lastFailedText && msg.status === "error" && (
                    <button
                      type="button"
                      style={styles.retryBtn}
                      onClick={() => sendMessage(lastFailedText)}
                    >
                      {t("chat.retry")}
                    </button>
                  )}
                </div>
              );
            }

            // Hide the silent init greeting once the user has started chatting
            if (msg.id === "init" && messages.length > 1) return null;

            return (
              <div
                key={msg.id}
                style={
                  msg.sender === "user"
                    ? styles.bubbleUser
                    : styles.bubbleAvatar
                }
              >
                {msg.text ||
                  (msg.status === "streaming"
                    ? t("chat.streamingPlaceholder")
                    : "")}
              </div>
            );
          })}

        {isStreaming && <div style={styles.typing}>{t("chat.typing")}</div>}
      </div>
      <ChatInput
        onSend={sendMessage}
        disabled={serviceUnavailable || isStreaming}
      />
    </div>
  );
}
